import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  type?: string;
  name?: string;
  imageUrl?: string;
  publishDate?: string;
  modifiedDate?: string;
  author?: string;
  category?: string;
  keywords?: string[];
  isBlogPost?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Xenira',
  description = 'Xenira: Advanced lipid-based drug delivery systems for enhanced bioavailability and therapeutic outcomes.',
  type = 'website',
  name = 'Xenira',
  imageUrl = '/xenira-logo.jpeg',
  publishDate,
  modifiedDate,
  author,
  category,
  keywords = ['drug delivery', 'lipid formulations', 'pharmaceutical technology', 'bioavailability enhancement', 'lipid-based systems', 'pharmaceutical development'],
  isBlogPost = false
}) => {
  const location = useLocation();
  const currentUrl = `https://xenirapharma.com${location.pathname}`;
  const absoluteImageUrl = imageUrl.startsWith('http') ? imageUrl : `https://xenirapharma.com${imageUrl}`;

  // Ensure keywords is always an array
  const keywordsArray = Array.isArray(keywords) ? keywords : [];

  // Enhanced Organization JSON-LD structured data with pharmaceutical details
  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'MedicalBusiness'],
    name: 'Xenira Pharmaceuticals',
    legalName: 'Xenira Pharmaceuticals Sp. z o.o.',
    url: 'https://xenirapharma.com',
    logo: 'https://xenirapharma.com/xenira-logo.jpeg',
    description: 'Advanced lipid-based drug delivery systems for enhanced bioavailability and therapeutic outcomes in pharmaceutical development.',
    foundingDate: '2020',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'ul. Marii Skłodowskiej-Curie 55',
      addressLocality: 'Wrocław',
      postalCode: '50-369',
      addressCountry: 'PL'
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@xenirapharma.com',
        availableLanguage: ['English', 'Polish']
      },
      {
        '@type': 'ContactPoint',
        contactType: 'legal',
        email: 'contact@xenirapharma.com',
        availableLanguage: 'English'
      }
    ],
    knowsAbout: [
      'Lipid-based drug delivery systems',
      'Pharmaceutical formulation development',
      'Bioavailability enhancement',
      'EU-GMP manufacturing',
      'Clinical trial materials'
    ]
  };

  // Enhanced BlogPosting JSON-LD structured data
  const blogPostStructuredData = isBlogPost && publishDate ? {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': currentUrl
    },
    headline: title,
    image: {
      '@type': 'ImageObject',
      url: absoluteImageUrl,
      width: 1200,
      height: 630
    },
    datePublished: publishDate,
    dateModified: modifiedDate || publishDate,
    author: {
      '@type': 'Organization',
      name: author || 'Xenira',
      url: 'https://xenirapharma.com'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Xenira',
      logo: {
        '@type': 'ImageObject',
        url: 'https://xenirapharma.com/xenira-logo.jpeg',
        width: 512,
        height: 512
      },
      url: 'https://xenirapharma.com'
    },
      description: description,
      keywords: keywordsArray.join(', '),
      articleSection: category,
    inLanguage: 'en-US',
    isAccessibleForFree: true
  } : null;

  // Combine keywords with any additional category terms
  const keywordString = category 
    ? [...keywordsArray, category.toLowerCase()].join(', ') 
    : keywordsArray.join(', ');

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={currentUrl} />
      <meta name="keywords" content={keywordString} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={isBlogPost ? 'article' : type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Xenira" />
      <meta property="og:locale" content="en_US" />
      {isBlogPost && category && <meta property="article:section" content={category} />}
      {isBlogPost && publishDate && <meta property="article:published_time" content={publishDate} />}
      {isBlogPost && modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
      {isBlogPost && <meta property="article:publisher" content="https://xenira.com" />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      <meta name="twitter:site" content="@xenira" />
      <meta name="twitter:creator" content="@xenira" />
      
      {/* LinkedIn specific */}
      <meta property="og:image:secure_url" content={absoluteImageUrl} />
      <meta name="author" content={author || name} />
      
      {/* Pinterest specific */}
      <meta name="pinterest:description" content={description} />
      <meta name="pinterest:image" content={absoluteImageUrl} />
      
      {/* Additional SEO meta tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
      
      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationStructuredData)}
      </script>
      
      {blogPostStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(blogPostStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
